import { useState } from 'react'
import { Avatar, Popover, TextInput, UnstyledButton } from '@mantine/core'
import { IconSearch, IconUserPlus, IconX } from '@tabler/icons-react'
import { useDebounce } from '@/hooks'
import { QUEUE_COLOR } from '@/pages/CareCoordination/queueTheme'
import { InitialsAvatar } from './InitialsAvatar'
import {
  useAutofocusOnOpen,
  usePatientSearch,
  useUpdateItemValue,
  type CellProps,
  type CellRef,
} from '../hooks'

// Monday-style patient picker: the cell shows the patient's avatar + name (or a
// ghost add-icon when empty); clicking opens a popover with the current patient
// as a removable chip, a search box, and the results — so a patient can be
// cleared or swapped, not just set once.
export const PatientCell = ({ item, column }: CellProps) => {
  const { update } = useUpdateItemValue(item)
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState('')
  const searchRef = useAutofocusOnOpen<HTMLInputElement>(opened)
  const debounced = useDebounce(search, 300)
  // Only query while the popover is open — otherwise Mantine's seeded search
  // value would fire a request per filled cell on load.
  const { data, isFetching } = usePatientSearch(opened ? debounced : '')
  const raw = item.values[column.id]
  const current: CellRef | null = typeof raw === 'object' && raw !== null ? raw : null
  const patients = data?.patients ?? []

  const pick = (patient: CellRef | null) => {
    void update(column.id, patient)
    setOpened(false)
    setSearch('')
  }

  const emptyMessage =
    debounced.trim().length < 2
      ? 'Type to search patients'
      : isFetching
        ? 'Searching…'
        : 'No patients found'

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={280}
      position="bottom-start"
      shadow="md"
      withinPortal
      trapFocus={false}
    >
      <Popover.Target>
        <UnstyledButton
          aria-label={current ? `Patient: ${current.display}` : 'Assign patient'}
          className="flex w-full cursor-pointer items-center justify-start gap-[9px] pr-3 text-left"
          onClick={() => setOpened((prev) => !prev)}
        >
          {/* Avatar + full name, matching the assignee cell: who the task is about
              is primary context, and initials alone made it guesswork from the row. */}
          {current ? (
            <>
              <InitialsAvatar name={current.display} tone="patient" />
              <span className="truncate text-[13.5px]" style={{ color: QUEUE_COLOR.ink }}>
                {current.display}
              </span>
            </>
          ) : (
            <>
              <IconUserPlus size={20} style={{ color: QUEUE_COLOR.ringIdle }} />
              <span className="text-[13.5px]" style={{ color: QUEUE_COLOR.subtle }}>
                No patient
              </span>
            </>
          )}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className="p-3">
        <div className="flex flex-col gap-2">
          {current && (
            <div className="flex">
              <span className="flex items-center gap-1.5 rounded-full bg-[#E3EDF7] py-1 pr-1.5 pl-1">
                <Avatar name={current.display} color="#275587" size={20} />
                <span className="text-xs">{current.display}</span>
                <button
                  type="button"
                  aria-label="Remove patient"
                  onClick={() => pick(null)}
                  className="cursor-pointer text-gray-500 transition hover:text-gray-700"
                >
                  <IconX size={12} />
                </button>
              </span>
            </div>
          )}

          <TextInput
            ref={searchRef}
            size="xs"
            placeholder="Search patients…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpened(false)
            }}
          />

          <div className="flex max-h-56 flex-col overflow-y-auto">
            {patients.map((patient) => (
              <button
                key={patient.reference}
                type="button"
                onClick={() => pick(patient)}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left transition hover:bg-gray-50"
              >
                <Avatar name={patient.display} color="#275587" size={24} />
                <span className="truncate text-sm">{patient.display}</span>
              </button>
            ))}
            {patients.length === 0 && (
              <p className="px-2 py-2 text-sm text-gray-400">{emptyMessage}</p>
            )}
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}
